import { Button } from '@design/components/button/Button';
import { Drawer } from '@design/components/drawer/Drawer';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ExpandableDrawer } from '@terminal/components/expandable-drawer/ExpandableDrawer';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useTrackEmailDrawerOpen } from '@terminal/hooks/use-track-email-drawer-open/useTrackEmailDrawerOpen';
import { useThemeCompanies } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { DownloadButton } from '@terminal/views/insight-feed/components/download-button/DownloadButton';
import { ScheduleDemoBanner } from '@terminal/views/insight-feed/components/schedule-demo-banner/ScheduleDemoBanner';
import { handleDownloadDrawer } from '@terminal/views/insight-feed/utils/handleDownloadDrawer';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemePerfBandChart } from './components/ThemePerfBandChart';
import styles from './MarketCatalystDrawer.module.scss';
import { MarketCatalystWidget } from './market-catalyst-widget/MarketCatalystWidget';
import { useMarketCatalystDrawer } from './useMarketCatalystDrawer';

const MARKET_CATALYST_DRAWER_WIDTH = 788;

export const MarketCatalystDrawer = () => {
  const { t } = useTranslation(['insights']);
  const { navigateToTheme } = useNavigateToThemeOverview();
  const { trackCatalystDrawerOpen } = usePosthogEvents();

  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );

  const [expandCitations, setExpandCitations] = useState(false);
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const { closeDrawer, isOpen, themeId, drawerType, insightId, utmSource } =
    useMarketCatalystDrawer();

  const { data: themeData } = useTheme({
    themeId: themeId ?? '',
  });

  const { data: themeCompanies } = useThemeCompanies({
    themeId: themeId ?? '',
    limit: 10,
    enabled: !!themeId,
  });

  useTrackEmailDrawerOpen({
    isDrawerOpen: isOpen,
    utmSource,
    trackingKey: insightId ? `${insightId}-${themeId ?? ''}` : undefined,
    onTrackEmailOpen: () => {
      if (!insightId) {
        return;
      }

      trackCatalystDrawerOpen({
        source: POSTHOG_TRACKING_SOURCES.EMAIL,
        type: 'market',
        insightId,
        themeIds: themeId ? [themeId] : undefined,
      });
    },
  });

  const handleNavigateToTheme = () => {
    if (themeId) {
      navigateToTheme({
        themeId,
        source: POSTHOG_TRACKING_SOURCES.MARKET_CATALYST_DRAWER,
      });
    }

    return null;
  };

  const handleDownload = () => {
    if (contentWrapperRef.current) {
      handleDownloadDrawer({
        contentToPrint: contentWrapperRef.current,
        id: themeId,
        expandAccordions: setExpandCitations,
        title: t('insights:catalyst.drawer.catalystInsight'),
      });
    }
  };

  const headerButtons = (
    <div className={styles.headerButtons}>
      {drawerType === 'thematic' && (
        <Button
          variant="empty"
          label={t('insights:catalyst.viewOnTheme')}
          iconName="Expand"
          size="xsmall"
          onClick={handleNavigateToTheme}
        />
      )}

      <DownloadButton onClick={handleDownload} />
    </div>
  );

  const getContent = () => (
    <div className={styles.contentWrapper} ref={contentWrapperRef}>
      <ErrorBoundaryWrapper>
        {insightId && (
          <MarketCatalystWidget
            insightId={insightId}
            expandCitations={expandCitations}
          />
        )}
      </ErrorBoundaryWrapper>
      <ScheduleDemoBanner />
    </div>
  );

  const entityTags = (themeCompanies ?? []).map(item => item.company);

  const fullViewContent = themeId && entityTags.length > 0 && (
    <ErrorBoundaryWrapper>
      <ThemePerfBandChart
        entityTags={entityTags}
        showHorizonSelector={false}
        themeId={themeId}
      />
    </ErrorBoundaryWrapper>
  );

  if (drawerType === 'thematic' && !isBasicOrLightUser) {
    return (
      <ExpandableDrawer
        isOpen={isOpen}
        className={styles.styledDrawer}
        handleCloseClick={closeDrawer}
        headerButton={headerButtons}
        defaultWidth={MARKET_CATALYST_DRAWER_WIDTH}
        label={t('insights:viewAllSection.whatElseIsMoving', {
          name: themeData?.name,
        })}
        fullViewContent={fullViewContent}
        subHeaderNode={
          themeData && (
            <div className={styles.themeNameWrapper}>
              <ThemeLogoIcon
                logo_url={themeData.logo_url}
                name={themeData.name}
              />
              <div className={styles.themeName}>{themeData.name}</div>
            </div>
          )
        }
      >
        {getContent()}
      </ExpandableDrawer>
    );
  }

  return (
    <Drawer
      className={styles.styledDrawer}
      data-testid="market-catalyst-drawer"
      isOpen={isOpen}
      handleCloseClick={closeDrawer}
      headerButton={headerButtons}
    >
      {getContent()}
    </Drawer>
  );
};
